import React, { useLayoutEffect, useRef, useState } from "react";
import { DistributorProduct, Product } from "@src/types/product";
import PxProductTile from "@src/components/widgets/PxProductTile";
import classnames from "classnames";

interface ProductListRowProps {
  products: Product[] | DistributorProduct[];
  cardsPerRow: number;
  imageSizePx?: number;
  tileClassName?: string; // Optional prop to specify tile width, if needed for layout purposes
  idContext?: string; // Optional prop to provide context for unique ID generation
  "data-testid"?: string;
}

export default function ProductListRow({
  products,
  cardsPerRow = 0,
  imageSizePx = 220,
  tileClassName,
  idContext,
  "data-testid": dataTestId = "product-list-row",
}: ProductListRowProps): React.JSX.Element {
  const listRowRef = useRef<HTMLDivElement>(null);
  const [visibleProductCount, setVisibleProductCount] = useState(
    products.length,
  );
  const [isAnyProductAddingToCart, setIsAnyProductAddingToCart] =
    useState(false);

  useLayoutEffect(() => {
    const el = listRowRef.current;
    if (!el) return;

    const tileSize = el.childNodes[0]?.getBoundingClientRect().width;

    const GAP = 10; // must match CSS gap (var(--margin-sm))

    const observer = new ResizeObserver(([entry]) => {
      const width = entry.contentRect.width;

      // how many tiles fit horizontally
      const tilesPerRow = Math.max(
        1,
        Math.floor((width + GAP) / (tileSize + GAP)),
      );

      setVisibleProductCount(Math.min(products.length, tilesPerRow));
    });

    observer.observe(el);

    return () => observer.disconnect();
  }, [products.length, imageSizePx]);

  const gridTemplateColumns = cardsPerRow
    ? `repeat(${cardsPerRow}, minmax(0, 1fr))`
    : `repeat(auto-fit, minmax(${imageSizePx - 20}px, 1fr))`;

  return (
    <div
      ref={listRowRef}
      className="flex-gap-sm"
      style={{
        display: "grid",
        gridTemplateColumns,
        gap: "var(--margin-sm)",
      }}
      data-testid={dataTestId}
    >
      {products.slice(0, visibleProductCount).map((product, index) => (
        <div key={`product-list-row-product-${product.id ?? index}-${index}`}>
          <PxProductTile
            className={classnames(
              "width-100-important max-width-300px",
              tileClassName,
            )}
            product={product}
            imageSizePx={imageSizePx}
            onSetIsAddingToCart={setIsAnyProductAddingToCart}
            disableAddToCartButton={isAnyProductAddingToCart}
            idContext={idContext}
          />
        </div>
      ))}
    </div>
  );
}
